import { useMemo, useState } from "react";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import {
  Box,
  Card,
  CardActionArea,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { OrderPaymentDto } from "@lib/ShiOneClient";
import type { OrderDrawerIdentifier } from "../types";
import { useOrderPaymentData } from "../useOrderPaymentData";

interface OrderDrawerPaymentProps {
  identifier: OrderDrawerIdentifier;
  enabled: boolean;
  disableGutters?: boolean;
}

function PaymentRow({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: React.ReactNode;
  emphasized?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.5,
      }}
    >
      <Typography variant="body1" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={emphasized ? 500 : "normal"}>
        {value}
      </Typography>
    </Box>
  );
}

export function PaymentContent({
  data,
  formatPrice,
}: {
  data: OrderPaymentDto;
  formatPrice?: (amount: number) => string;
}) {
  const savedFormatter = useMemo(
    () => createSavedCurrencyFormatter(data.currencyCode),
    [data.currencyCode],
  );
  const format = formatPrice ?? savedFormatter.format;

  return (
    <>
      {/* Line items + Total with theme-aware background for both light and dark modes */}
      <Box sx={{ bgcolor: "action.hover", mx: 2, px: 2, my: 2, py: 1.5, borderRadius: 1 }}>
        <PaymentRow label="Subtotal" value={format(data.subTotal ?? 0)} />
        <PaymentRow
          label="Shipping"
          value={format(data.freight ?? 0)}
        />
        <PaymentRow label="Tax" value={format(data.tax ?? 0)} />
        <PaymentRow
          label="Recycling Fee"
          value={format(data.recyclingFee ?? 0)}
        />
        <Divider sx={{ my: 0.5 }} />
        <PaymentRow label="Total" value={format(data.total ?? 0)} emphasized />
      </Box>

      {/* Payment method */}
      {data.hasCreditCard && data.creditCardLast4Digits && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Payment Method
            </Typography>
            <Typography variant="body1" fontWeight="normal">
              •••• {data.creditCardLast4Digits}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}

export function OrderDrawerPayment({
  identifier,
  enabled,
  disableGutters = false,
}: Readonly<OrderDrawerPaymentProps>) {
  const [expanded, setExpanded] = useState(true);
  const { paymentData } = useOrderPaymentData({
    identifier,
    enabled,
  });

  if (!paymentData) return null;

  return (
    <Card elevation={1} sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
      <CardActionArea
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" sx={{ textTransform: "uppercase", fontWeight: "normal" }}>
          Payment
        </Typography>
        <ExpandMoreIcon
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            color: "action.active",
          }}
        />
      </CardActionArea>
      <Collapse in={expanded}>
        <PaymentContent data={paymentData} />
      </Collapse>
    </Card>
  );
}
